import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Page, Card, BlockStack, Text, Badge, InlineStack, Box, Banner, Spinner,
  TextField, Button, IndexTable,
} from '@shopify/polaris';
import { useQuery } from '@tanstack/react-query';
import { getVehicleMatches, type FitmentMatch } from '../api/matches';
import { getVehicle } from '../api/vehicles';
import { ConfidenceBadge } from '../components/ConfidenceBadge';

export function FitmentDebug() {
  const { id } = useParams<{ id: string }>();
  const numericId = id ? parseInt(id, 10) : null;
  const navigate = useNavigate();
  const [lookupId, setLookupId] = useState('');

  if (!numericId) {
    return (
      <Page title="Fitment debug" subtitle="Pick a vehicle to inspect its precomputed wheel matches">
        <Card>
          <BlockStack gap="300">
            <Text as="p" variant="bodyMd">Enter a vehicle ID to see all matched wheels with confidence + reasons.</Text>
            <InlineStack gap="200">
              <TextField label="Vehicle ID" labelHidden type="number" value={lookupId}
                onChange={setLookupId} autoComplete="off" placeholder="123" />
              <Button variant="primary" disabled={!lookupId}
                onClick={() => navigate(`/fitment-debug/${lookupId}`)}>Inspect</Button>
              <Button onClick={() => navigate('/vehicles')}>Browse vehicles</Button>
            </InlineStack>
          </BlockStack>
        </Card>
      </Page>
    );
  }

  return <VehicleMatches vehicleId={numericId} />;
}

function VehicleMatches({ vehicleId }: { vehicleId: number }) {
  const navigate = useNavigate();
  const vehicle = useQuery({
    queryKey: ['vehicle', vehicleId],
    queryFn: () => getVehicle(vehicleId),
  });
  const matches = useQuery({
    queryKey: ['matches', vehicleId],
    queryFn: () => getVehicleMatches(vehicleId),
  });

  if (vehicle.isLoading || matches.isLoading) {
    return <Page title="Fitment debug"><Spinner /></Page>;
  }
  if (vehicle.error || matches.error) {
    return (
      <Page title="Fitment debug">
        <Banner tone="critical">{(vehicle.error ?? matches.error as Error).message}</Banner>
      </Page>
    );
  }

  const v = vehicle.data!;
  const rows = matches.data?.data ?? [];

  const byConfidence = rows.reduce((acc, m) => {
    const key = m.confidence ?? 'unknown';
    acc[key] = (acc[key] ?? 0) + 1;
    return acc;
  }, {} as Record<string, number>);

  return (
    <Page
      title={`${v.year} ${v.make} ${v.model}`}
      subtitle={v.submodel ? `${v.submodel} · fitment debug` : 'Fitment debug'}
      backAction={{ content: 'Vehicle', onAction: () => navigate(`/vehicles/${vehicleId}`) }}
    >
      <BlockStack gap="400">
        <Card>
          <BlockStack gap="200">
            <Text as="h3" variant="headingMd">Vehicle summary</Text>
            <InlineStack gap="400" wrap>
              <SummaryField label="Body" value={v.body_config ?? '—'} />
              <SummaryField label="BP canonical" value={v.bolt_pattern_canonical} />
              <SummaryField label="Hub front" value={v.hub_bore_front_mm ? `${v.hub_bore_front_mm}mm` : '—'} />
              <SummaryField label="Hub rear" value={v.hub_bore_rear_mm ? `${v.hub_bore_rear_mm}mm` : '—'} />
              <SummaryField label="Diameter" value={`${v.wheel_diameter_min_in ?? '?'}–${v.wheel_diameter_max_in ?? '?'}"`} />
              <SummaryField label="Front offset" value={`${v.front_offset_min_mm ?? '?'}…${v.front_offset_max_mm ?? '?'}`} />
              <SummaryField label="Rear offset" value={`${v.rear_offset_min_mm ?? '?'}…${v.rear_offset_max_mm ?? '?'}`} />
            </InlineStack>
          </BlockStack>
        </Card>

        <Card>
          <BlockStack gap="300">
            <InlineStack align="space-between" blockAlign="center">
              <Text as="h3" variant="headingMd">{rows.length} matched wheel{rows.length === 1 ? '' : 's'}</Text>
              <InlineStack gap="200">
                {Object.entries(byConfidence).map(([c, n]) => (
                  <InlineStack key={c} gap="100"><ConfidenceBadge confidence={c} /><Text as="span" variant="bodySm">{n}</Text></InlineStack>
                ))}
              </InlineStack>
            </InlineStack>
            {rows.length === 0 ? (
              <Box padding="400"><Text as="p" alignment="center" tone="subdued">No precomputed matches for this vehicle.</Text></Box>
            ) : (
              <IndexTable
                resourceName={{ singular: 'match', plural: 'matches' }}
                itemCount={rows.length}
                selectable={false}
                headings={[
                  { title: 'Wheel' }, { title: 'SKU' }, { title: 'Size' }, { title: 'Offset' },
                  { title: 'Hub' }, { title: 'BPs' }, { title: 'Position' },
                  { title: 'Score' }, { title: 'Confidence' }, { title: 'Flags' },
                ]}
              >
                {rows.map((m, i) => <MatchRow key={m.match_id} row={m} position={i} />)}
              </IndexTable>
            )}
          </BlockStack>
        </Card>
      </BlockStack>
    </Page>
  );
}

function MatchRow({ row, position }: { row: FitmentMatch; position: number }) {
  const [expanded, setExpanded] = useState(false);
  const ringRequired = (row.match_reasons as any)?.hub === 'ring_required';
  return (
    <>
      <IndexTable.Row id={String(row.match_id)} position={position} onClick={() => setExpanded((e) => !e)}>
        <IndexTable.Cell><Text as="span" variant="bodyMd" fontWeight="semibold">{row.brand} {row.model}</Text></IndexTable.Cell>
        <IndexTable.Cell><Text as="span" variant="bodySm" tone="subdued">{row.sku}</Text></IndexTable.Cell>
        <IndexTable.Cell>{row.diameter_in}×{row.width_in}</IndexTable.Cell>
        <IndexTable.Cell>{row.offset_mm > 0 ? `+${row.offset_mm}` : row.offset_mm}</IndexTable.Cell>
        <IndexTable.Cell>{row.hub_bore_mm}mm</IndexTable.Cell>
        <IndexTable.Cell>
          <InlineStack gap="100" wrap>
            {row.bolt_patterns_canonical.map((c) => <Badge key={c} tone="info">{c}</Badge>)}
          </InlineStack>
        </IndexTable.Cell>
        <IndexTable.Cell>{row.position}</IndexTable.Cell>
        <IndexTable.Cell>{row.match_score?.toFixed(2) ?? '—'}</IndexTable.Cell>
        <IndexTable.Cell><ConfidenceBadge confidence={row.confidence} /></IndexTable.Cell>
        <IndexTable.Cell>
          <InlineStack gap="100" wrap>
            {ringRequired && <Badge tone="warning">Ring required</Badge>}
            {row.is_override && <Badge tone="attention">Override</Badge>}
            {row.is_dually && <Badge>Dually</Badge>}
          </InlineStack>
        </IndexTable.Cell>
      </IndexTable.Row>
      {expanded && (
        <IndexTable.Row id={`${row.match_id}-details`} position={position + 0.5} rowType="child">
          <IndexTable.Cell colSpan={10}>
            <Box padding="300" background="bg-surface-secondary">
              <BlockStack gap="200">
                <Text as="p" variant="bodySm" fontWeight="semibold">match_reasons</Text>
                <code style={{ fontSize: 11, whiteSpace: 'pre-wrap' }}>
                  {JSON.stringify(row.match_reasons, null, 2)}
                </code>
              </BlockStack>
            </Box>
          </IndexTable.Cell>
        </IndexTable.Row>
      )}
    </>
  );
}

function SummaryField({ label, value }: { label: string; value: string }) {
  return (
    <Box minWidth="120px">
      <Text as="p" variant="bodySm" tone="subdued">{label}</Text>
      <Text as="p" variant="bodyMd">{value}</Text>
    </Box>
  );
}
